import { Component } from '../../scripts/component';
import { removeTrapFocus } from '../../scripts/focus';
import { debounce, isClickedOutside, onAnimationEnd } from '../../scripts/utils';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   *
   * @param {Event} [event] - The user event that opened the dialog (used for search modal focus).
   */
  showDialog(event) {
    const dialog = /** @type {HTMLDialogElement} */ (this.querySelector(':scope > [ref="dialog"]'));
    if (dialog.open) return;

    const isSearchModal = this.id === 'search-modal';

    if (isSearchModal) {
      this.#prepareSearchFromMenu(event);
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    const openDialog = () => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      if (isSearchModal) {
        this.#focusSearchInput();
      }
    };

    // Search modal must open and focus synchronously so iOS opens the keyboard on first tap.
    if (isSearchModal) {
      openDialog();
    } else {
      // Prevent layout thrashing by separating DOM reads from DOM writes
      requestAnimationFrame(openDialog);
    }
  }

  /**
   * Closes the menu drawer and releases focus trap when opening search from the mobile menu.
   *
   * @param {Event} [event]
   */
  #prepareSearchFromMenu(event) {
    if (!(event?.target instanceof Element)) return;

    const headerDrawer =
      event.target.closest('header-drawer')
      ?? (event.target.closest('#slide-in-group .menu-drawer') ? document.getElementById('header-drawer') : null);
    if (!headerDrawer) return;

    removeTrapFocus();

    if (typeof headerDrawer.close === 'function') {
      headerDrawer.close();
    }
  }

  /**
   * Focuses the predictive search input inside the search modal.
   */
  #focusSearchInput() {
    const searchInput = this.querySelector('[ref="searchInput"]');

    if (searchInput instanceof HTMLInputElement) {
      searchInput.focus({ preventScroll: true });
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const dialog = /** @type {HTMLDialogElement} */ (this.querySelector(':scope > [ref="dialog"]'));
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const dialog = /** @type {HTMLDialogElement} */ (this.querySelector(':scope > [ref="dialog"]'));

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
